import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { GenericModal, FeedbackCampaignForm } from '@org/ui';
import { trpc } from '@org/clients/school';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';

export const Route = createFileRoute('/feedback/new')({
  component: FeedbackCreateRoute,
});

function FeedbackCreateRoute() {
  const navigate = useNavigate({ from: '/feedback/new' });
  const utils = trpc.useUtils();
  const { t } = useTranslation('admin');

  const searchEvents = useCallback(
    async (query: string) => {
      if (query.length < 3) return [];
      return utils.feedback.searchEvents.fetch({ query });
    },
    [utils],
  );

  const searchGroups = useCallback(
    async (query: string) => {
      if (query.length < 3) return [];
      const results = await utils.groups.searchActiveGroups.fetch({ search: query });
      return results.map((g) => ({
        value: g.groupId,
        label: g.description ?? g.groupId,
      }));
    },
    [utils],
  );

  const searchUsers = useCallback(
    async (query: string) => {
      if (query.length < 3) return [];
      const results = await utils.users.searchActiveUsers.fetch({ search: query });
      return results.map((u) => ({
        value: u.userId,
        label: `${u.firstName} ${u.lastName}`,
      }));
    },
    [utils],
  );

  const createMutation = trpc.feedback.create.useMutation({
    onSuccess: async () => {
      await utils.feedback.list.invalidate();
      toast.success(t('common.toasts.createSuccess', { entity: 'Campaign' }));
      handleClose();
    },
    onError: () => {
      toast.error(t('common.toasts.actionError', { action: 'create', entity: 'campaign' }));
    },
  });

  const handleClose = () => {
    navigate({ to: '/feedback', replace: true });
  };

  return (
    <GenericModal
      show
      onClose={handleClose}
      title={t('feedback.form.createTitle')}
      size="xl"
      centered
      body={
        <FeedbackCampaignForm
          onSubmit={(values) => createMutation.mutate(values)}
          onClose={handleClose}
          isSubmitting={createMutation.isPending}
          onEventSearch={searchEvents}
          onSearchGroups={searchGroups}
          onSearchUsers={searchUsers}
          labels={{
            title: t('feedback.form.title'),
            description: t('feedback.form.description'),
            campaignType: t('feedback.form.campaignType'),
            targetType: t('feedback.form.targetType'),
            questions: t('feedback.form.questions'),
            addQuestion: t('feedback.form.addQuestion'),
            questionPlaceholder: t('feedback.form.questionPlaceholder'),
            templateHint: t('feedback.form.templateHint'),
            submit: t('feedback.form.submit'),
            cancel: t('feedback.form.cancel'),
            recurringExplanation: t('feedback.form.recurringExplanation'),
          }}
        />
      }
      footer={<></>}
    />
  );
}
